// SalaryComponent
import { useState } from "react";
import { Reactable } from "../../components/ui/reactable";
import { Button } from "../../components/ui/button";
import { Edit, Trash, Wallet } from "lucide-react";
import { SALARY_COMPONENT } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { ConfirmPopupModal, DialogModal } from "../../components/ui/dialogModal";
import { Input, Textarea } from "../../components/ui/input";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { checkUserPermissionFunc, truncateText } from "../../components/common/CommonFunctions";
import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Select, Checkbox } from "antd";

// Component types from your old code
const salaryComponentType = [
    { value: "earning", text: "Earning" },
    { value: "deduction", text: "Deduction" }
];

const SalaryComponent = () => {
    const { create, update } = useApiMethods();
    const [SalaryComponentsData, setSalaryComponentsData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [serverTrigger, setServerTrigger] = useState(false);

    const [openDialog, setOpenDialog] = useState(false);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [loading, setLoading] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editId, setEditId] = useState(null);

    const { add: addPermission, edit: editPermission, draft: draftPermission } = checkUserPermissionFunc('configuration', 'salary component', 'SubMenu')

    // Form state for salary component - matching old code structure
    const [formData, setFormData] = useState({
        code: "",
        salary_component_name: "",
        component_type: "",
        is_active: true,
        employee_share: true,
        employer_share: false,
        amount: false,
        is_draft: false,
        is_default: false
    });

    // Reset form function
    const resetForm = () => {
        setFormData({
            code: "",
            salary_component_name: "",
            component_type: "",
            is_active: true,
            employee_share: true,
            employer_share: false,
            amount: false,
            is_draft: false,
            is_default: false
        });
        setEditMode(false);
        setEditId(null);
        setOpenDialog(false);
        setSelectedRow(null);
    };

    // Handle form field changes
    const handleInputChange = (field, value) => {
        const updatedFormData = {
            ...formData,
            [field]: value
        };

        // Reset deduction-specific fields when component type changes to "earning"
        if (field === "component_type" && value === "earning") {
            updatedFormData.employee_share = false;
            updatedFormData.employer_share = false;
            updatedFormData.amount = false;
        }

        setFormData(updatedFormData);
    };

    // Handle key down for salary component name - from old code
    const handleKeyDown = (e) => {
        const allowedKeys = ['Backspace', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'Delete', 'Shift'];
        if (allowedKeys.includes(e.key) || /^[a-zA-Z\s]$/.test(e.key)) {
            return;
        }
        e.preventDefault();
    };

    // Table columns for salary component data
    const SalaryComponentColumns = [
        { 
            accessor: "code", 
            Header: "Salary Component Code", 
            sortable: true,
            width: "150px",
        },
        {
            accessor: "salary_component_name",
            Header: "Salary Component Name",
            sortable: true,
            filterable: true,
            width: "180px",
        },
        {
            accessor: "component_type",
            Header: "Component Type",
            sortable: true,
            filterable: true,
            width: "120px",
            Cell: ({ value }) => {
                const type = salaryComponentType.find(type => type.value === value);
                return type ? type.text : "-";
            }
        },
        {
            accessor: "is_active",
            Header: "Status",
            sortable: false,
            filterable: true,
            width: "100px",
            Cell: ({ value }) => (
                <span
                    className={cn(
                        "px-2 py-1 text-xs font-medium rounded-full",
                        value
                            ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                            : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                    )}
                >
                    {value ? "Active" : "Inactive"}
                </span>
            ),
        },
        ...(editPermission ? [
            {
                accessor: "action",
                Header: "Actions",
                sortable: false,
                width: "100px",
                Cell: ({ row }) => {
                    const handleOpenDialog = () => {
                        const rowData = row;
                        setEditMode(true);
                        setEditId(rowData?.code);
                        setFormData({
                            id: rowData?.id || null,
                            code: rowData?.code || "",
                            salary_component_name: rowData?.salary_component_name || "",
                            component_type: rowData?.component_type || "",
                            is_active: rowData?.is_active ?? true,
                            employee_share: rowData?.employee_share || false,
                            employer_share: rowData?.employer_share || false,
                            amount: rowData?.amount || false,
                            is_draft: rowData?.is_draft || false,
                            is_default: rowData?.is_default || false
                        });
                        setOpenDialog(true);
                    };

                    const handleDeleteClick = (row) => {
                        setSelectedRow(row);
                        setIsDeleteConfirmOpen(true);
                    };

                    return (
                        <div className="flex items-center gap-2">
                            {/* Edit Button */}
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-sidebar-primary hover:bg-blue-50 dark:hover:bg-gray-700"
                                onClick={handleOpenDialog}
                                disabled={row?.is_default}
                            >
                                <Edit className="h-4 w-4" />
                            </Button>

                            {/* Delete Button (only if row is draft) */}
                            {row?.is_draft && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30"
                                    onClick={() => handleDeleteClick(row)}
                                >
                                    <Trash className="h-4 w-4" />
                                </Button>
                            )}
                        </div>
                    );
                },
            },
        ] : [])
    ];

    // Prepare payload for API - matching old code logic
    const preparePayload = (isDraft = false) => {
        const payload = {
            ...formData,
            employee_share: formData.component_type === "earning" ? false : formData.employee_share,
            employer_share: formData.component_type === "earning" ? false : formData.employer_share,
            amount: formData.component_type === "earning" ? false : formData.amount,
            is_draft: isDraft
        };
        
        return payload;
    };

    // Save as draft function
    const handleDraft = async () => {
        // Validation
        if (!formData.salary_component_name?.trim()) {
            ToastMsg("error", "Salary component name is required");
            return;
        }
        if (!formData.component_type) {
            ToastMsg("error", "Component type is required");
            return;
        }

        try {
            setLoading(true);
            const payload = preparePayload(true);

            const response = await create(SALARY_COMPONENT, payload);

            if (response?.status === "success") {
                ToastMsg("success", response.message || "Salary component saved to draft successfully");
                resetForm();
                setServerTrigger((prev) => !prev);
            } else {
                ToastMsg("error", response?.message || "Failed to save draft");
            }
        } catch (error) {
            console.error("Error saving draft:", error);
            ToastMsg("error", "Failed to save draft");
        } finally {
            setLoading(false);
        }
    };

    // Delete draft function
    const handleDraftDelete = async () => {
        try {
            setLoading(true);
            if (!selectedRow) return;

            const payload = {
                id: selectedRow.id,
                is_active: false,
                draft_delete: true,
                is_draft: true
            };

            const response = await update(SALARY_COMPONENT, payload);

            if (response?.status === "success") {
                ToastMsg("success", response.message || "Draft salary component deleted successfully");
                setServerTrigger((prev) => !prev);
                setIsDeleteConfirmOpen(false);
                setSelectedRow(null);
            } else {
                ToastMsg("error", response?.message || "Failed to delete draft salary component");
            }
        } catch (error) {
            console.error("Error deleting draft:", error);
            ToastMsg("error", "Failed to delete draft salary component");
        } finally {
            setLoading(false);
        }
    };

    // Save or update function
    const handleSave = async () => {
        // Validation
        if (!formData.salary_component_name?.trim()) {
            ToastMsg("error", "Salary component name is required");
            return;
        }
        if (!formData.component_type) {
            ToastMsg("error", "Component type is required");
            return;
        }

        try {
            setLoading(true);
            const payload = preparePayload(false);

            let response;

            if (editMode && formData.id) {
                response = await update(SALARY_COMPONENT, payload);
            } else {
                response = await create(SALARY_COMPONENT, payload);
            }

            if (response?.status === "success") {
                ToastMsg("success", response.message || `Salary component ${editMode ? 'updated' : 'saved'} successfully`);
                resetForm();
                setServerTrigger((prev) => !prev);
            } else {
                ToastMsg("error", response?.message || `Failed to ${editMode ? 'update' : 'save'} salary component`);
            }
        } catch (error) {
            console.error("Error saving salary component:", error);
            ToastMsg("error", `Failed to ${editMode ? 'update' : 'save'} salary component`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <ContainerCard>
            <ModuleTitleCard
                Icon={Wallet}
                title={'Salary Component'}
                subtitle="Manage and configure salary components"
                totalRecords={totalRecords}
                buttonEnable={!!addPermission}
                setOpenDialog={setOpenDialog}
                buttonName={'Add Component'}
            />

            <Reactable
                columns={SalaryComponentColumns}
                data={SalaryComponentsData}
                setData={setSalaryComponentsData}
                setTotalCount={setTotalRecords}
                url={SALARY_COMPONENT}
                serverSide={true}
                serverTrigger={serverTrigger}
                tableHeight="calc(100vh - 320px)"
                rowSize={editPermission ? "sm" : null}
                isCSVRequired={true}
                isPDFRequired={true}
                isDraftRequired={!!draftPermission}
                isDateRequired={false}
            />

            {/* Add/Edit Modal with salary component fields */}
            <DialogModal
                open={openDialog}
                onClose={resetForm}
                onDraft={handleDraft}
                onSave={handleSave}
                title={editMode ? "Edit Salary Component" : "Add Salary Component"}
                closeLabel="Cancel"
                saveLabel={editMode ? "Update" : "Save"}
                draftLabel="Draft"
                editMode={editMode}
                loading={loading}
                width="600px"
            >
                <form className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        {/* Salary Component Code (only in edit mode) */}
                        {editMode && (
                            <div className="space-y-2 col-span-2">
                                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                    Salary Component Code
                                </label>
                                <Input
                                    value={formData.code}
                                    disabled
                                    placeholder="Auto-generated code"
                                    className={cn(
                                        "w-full rounded-md transition-colors duration-200",
                                        "border-gray-300 dark:border-gray-600",
                                        "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white",
                                        "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                                    )}
                                />
                            </div>
                        )}

                        {/* Salary Component Name */}
                        <div className="space-y-2 col-span-2">
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                Salary Component Name <span className="text-red-500">*</span>
                            </label>
                            <Input
                                value={formData.salary_component_name}
                                onChange={(e) => handleInputChange("salary_component_name", e.target.value)}
                                placeholder="Enter salary component name"
                                disabled={loading || formData.is_default}
                                onKeyDown={handleKeyDown}
                                className={cn(
                                    "w-full rounded-md transition-colors duration-200",
                                    "border-gray-300 dark:border-gray-600",
                                    "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                    "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                    "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                                )}
                            />
                        </div>

                        {/* Component Type */}
                        <div className="space-y-2 col-span-2">
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                Component Type <span className="text-red-500">*</span>
                            </label>
                            <Select
                                value={formData.component_type}
                                onChange={(value) => handleInputChange("component_type", value)}
                                options={salaryComponentType.map(type => ({
                                    value: type.value,
                                    label: type.text
                                }))}
                                placeholder="Select component type"
                                disabled={loading || formData.is_default}
                                className="w-full"
                                size="large"
                            />
                        </div>

                        {/* Deduction Options - Only show for deduction type */}
                        {formData.component_type === "deduction" && (
                            <div className="space-y-3 col-span-2 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                    Deduction Options
                                </label>
                                <div className="grid grid-cols-3 gap-4">
                                    <Checkbox
                                        checked={formData.employee_share}
                                        onChange={(e) => handleInputChange("employee_share", e.target.checked)}
                                        disabled={loading || formData.is_default}
                                        className="text-sm"
                                    >
                                        Employee Share
                                    </Checkbox>
                                    <Checkbox
                                        checked={formData.employer_share}
                                        onChange={(e) => handleInputChange("employer_share", e.target.checked)}
                                        disabled={loading || formData.is_default}
                                        className="text-sm"
                                    >
                                        Employer Share
                                    </Checkbox>
                                    <Checkbox
                                        checked={formData.amount}
                                        onChange={(e) => handleInputChange("amount", e.target.checked)}
                                        disabled={loading || formData.is_default}
                                        className="text-sm"
                                    >
                                        Amount
                                    </Checkbox>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Active Status */}
                    <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                        <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                            Active Status
                        </label>
                        <div className="flex items-center gap-3">
                            <span className={cn(
                                "text-xs font-medium",
                                formData.is_active
                                    ? "text-green-600 dark:text-green-400"
                                    : "text-gray-500 dark:text-gray-400"
                            )}>
                                {formData.is_active ? "Active" : "Inactive"}
                            </span>
                            <button
                                type="button"
                                onClick={() => handleInputChange("is_active", !formData.is_active)}
                                className={cn(
                                    "relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200",
                                    formData.is_active ? "bg-sidebar-primary" : "bg-gray-300 dark:bg-gray-600",
                                    loading || formData.is_default ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                                )}
                                disabled={loading || formData.is_default}
                            >
                                <span
                                    className={cn(
                                        "inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-200",
                                        formData.is_active ? "translate-x-4" : "translate-x-1"
                                    )}
                                />
                            </button>
                        </div>
                    </div>

                    {formData.is_default && (
                        <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                            <p className="text-xs text-yellow-700 dark:text-yellow-300">
                                This is a default component and cannot be modified.
                            </p>
                        </div>
                    )}

                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        <span className="text-red-500">*</span> indicates required field
                    </p>
                </form>
            </DialogModal>

            {/* Delete Confirmation Modal */}
            <ConfirmPopupModal
                open={isDeleteConfirmOpen}
                onClose={() => {
                    setIsDeleteConfirmOpen(false);
                    setSelectedRow(null);
                }}
                onConfirm={handleDraftDelete}
                title="Delete Draft Salary Component"
                description="Are you sure you want to delete this draft salary component? This action cannot be undone."
                type="warning"
                confirmLabel="Delete"
                destructive={true}
                loading={loading}
            />
        </ContainerCard>
    );
};

export default SalaryComponent;